import React, { useEffect, useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import classNames from 'classnames'
import { type CreateFundingRequestPrivateDto, useCreate, useUpdateFundingRequest } from '@/api/kubik/credit'
import type { FundingRequestData } from '@/api/kubik/monolith'
import ApplyingFinancingAgentFail from '@/components/composed/landingBlocks/LandingApplyingOfferFormNew/components/ApplyingFinancingAgentFail/ApplyingFinancingAgentFail'
import ApplyingFinancingAgentSuccessNew from '@/components/composed/landingBlocks/LandingApplyingOfferFormNew/components/ApplyingFinancingAgentSuccessNew/ApplyingFinancingAgentSuccess'
import { DEFAULT_VALUE, EFormStatus } from '@/components/composed/landingBlocks/LandingApplyingOfferFormNew/constant'
import type {
  TLandingApplyingOfferAgentFormProps,
  TLandingApplyingOfferAgentFormValues,
} from '@/components/composed/landingBlocks/LandingApplyingOfferFormNew/types'
import { LandingApplyingOfferPreferredConnections } from '@/components/composed/landingBlocks/LandingApplyingOfferPreferredConnections'
import { openModal } from '@/components/composed/Modal/actions'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import { FinancingUxFeedbackModal } from '@/components/composed/modals/FinancingUxFeedbackModal/FinancingUxFeedbackModal'
import type { IFinancingUxFeedbackModalProps } from '@/components/composed/modals/FinancingUxFeedbackModal/types'
import { Button } from '@/components/ui'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { Checkbox, RegionSelect, TextInput } from '@/components/ui/ReactHookFormFields'
import { FormValuesFormatter } from '@/constants/financing'
import { isUserAgrarian } from '@/logic/auth/reducer'
import { useData } from '@/logic/data'
import { checkRegionForMFO } from '@/logic/financing/helpers'
import { getLegalDocumentUrl } from '@/logic/legalDocuments/helpers'
import { pushGtmEventOnRouteAuthFromFormOrder, pushGtmEventOnSubmitOrder } from '@/logic/metrika/financing/gtmHelpers'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import type { RootState } from '@/redux/interfaces'
import { EFinancingOfferType } from '@/types'
import type { TLegalDocuments } from '@/types/legalDocuments'
import { ELegalDocumentType } from '@/types/legalDocuments'
import { formatLocalizedString } from '@/utils/formatUtils'
import { getApplyingFinancingOfferFormInitialValues } from '@/utils/getApplyingFinancingOfferFormInitialValues'
import { isApplyingOfferRequestBody } from '@/utils/hooks/applyingFinancingOffer/typeguard'
import { useRegionsMicrocreditOrganizations } from '@/utils/hooks/applyingFinancingOffer/useRegionsMicrocreditOrganizations'
import { useApplyingFinancingOfferFormNew } from '@/utils/hooks/applyingFinancingOfferNew/useApplyingFinancingOfferFormNew'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { LandingAnimation } from '../LandingAnimation'
import { LANDING_ANIMATION } from '../LandingAnimation/constants'
import { messages } from './messages'
import './styles.scss'

addModalToWellKnown({ FinancingUxFeedbackModal })

const mapState = (state: RootState) => ({
  isAgrarian: isUserAgrarian()(state),
  userGtmInfo: getUserGtmInfo()(state),
})

export const LandingApplyingOfferFormNew: React.FC<TLandingApplyingOfferAgentFormProps> = props => {
  const {
    formId,
    offerType,
    productId,
    title,
    description,
    withAnimation,
    initialRegion,
    initialPrice = '',
    className = '',
    wrapperClassName = '',
    rightBlockClassName = '',
    successClassName = '',
    successDescriptionClassName = '',
    successButtonClassName = '',
    errorClassName = '',
    communicationMethodClassName = '',
    textInputModifiers = [],
    regionSelectModifiers = ['light-grey'],
    navItems,
    banner,
    gtmBlockName,
    withLoanAmount = true,
    submitButttonText = messages.form.submit,
    requestMapper,
    isShowUxFeedbackModal = false,
    onSuccess,
    onError,
  } = props
  const { data: legalDocuments = [] } = useData<TLegalDocuments>('legalDocuments')
  const { isMobile } = useDeviceType()
  const [formStatus, setFormStatus] = useState<EFormStatus>(EFormStatus.FORM)
  const { isAgrarian, userGtmInfo } = useSelector(mapState)
  const { userRegion } = useUserRegion()
  const { isAuthenticated, openAuthModal } = useAuthenticateUser()
  const shouldDisableButton = isAuthenticated && !isAgrarian
  const { setOptions, formValues: values, profile } = useApplyingFinancingOfferFormNew({ productId, offerType, requestMapper })
  const [fundingRequestId, setFundingRequestId] = useState<number | null>(null)

  const methods = useForm<TLandingApplyingOfferAgentFormValues>({
    defaultValues: {
      price: initialPrice ?? DEFAULT_VALUE.price,
      phone: isAuthenticated ? profile.phone : '',
      regions: initialRegion ?? DEFAULT_VALUE.regions,
      communicationMethod: '',
      agreement: false,
    },
  })

  const dispatch = useDispatch()

  const { control, reset, getValues, setValue } = methods

  const handleSuccess = () => {
    onSuccess?.()
    setFormStatus(EFormStatus.SUCCESS)

    if (isShowUxFeedbackModal) {
      dispatch(
        openModal({
          options: { dialogId: 'FinancingUxFeedbackModal', shouldBlockedScroll: false },
          contentOptions: { isHasSkipButton: false } as IFinancingUxFeedbackModalProps['options'],
        }),
      )
    }
  }

  const handleMethod = () => {
    setFormStatus(EFormStatus.COMMUNICATION_METHOD)
  }

  const handleError = (err: unknown) => {
    onError?.()
    setFormStatus(EFormStatus.FAIL)
    console.error('Error in form submissions:', err)
  }

  const resetForm = () => {
    reset({
      phone: profile.phone,
    })

    setFormStatus(EFormStatus.FORM)
  }

  const { isLoading, mutate: createFundingRequest } = useCreate({
    mutation: {
      onSuccess: (response, variables) => {
        const requestId = response.data?.fundingRequests?.[0]?.id

        if (requestId) {
          setFundingRequestId(requestId)

          const createdRequests = (response.data.fundingRequests ?? []) as FundingRequestData[]

          pushGtmEventOnSubmitOrder(userGtmInfo, userRegion.name, createdRequests, variables.productIds as FundingProduct[], gtmBlockName)
        } else {
          handleError(response.status)
        }
        handleMethod()
      },
      onError: handleError,
    },
  })

  const { isLoading: isLoadingUpdate, mutate: updateFundingRequest } = useUpdateFundingRequest({
    mutation: {
      onSuccess: handleSuccess,
      onError: handleError,
    },
  })

  useEffect(() => {
    setValue('price', initialPrice)
  }, [initialPrice, setValue])

  useEffect(() => {
    setValue('regions', initialRegion ?? DEFAULT_VALUE.regions)
  }, [initialRegion, setValue])

  useEffect(() => {
    if (isAuthenticated && profile?.phone && !methods.getValues('regions')) {
      reset({
        phone: profile.phone,
      })
    }
  }, [getValues, profile.phone, reset, isAuthenticated, methods])

  const value = getApplyingFinancingOfferFormInitialValues({
    offerType,
    productId,
    profile,
    formValues: values,
  })

  const { isMFOProduct, regionsAvailableExpressMFO } = useRegionsMicrocreditOrganizations({
    productId,
  })

  const onSubmit: SubmitHandler<TLandingApplyingOfferAgentFormValues> = async formValue => {
    const formattedValue = value || FormValuesFormatter[offerType]?.(value)

    // @ts-ignore
    const body: CreateFundingRequestPrivateDto = {
      ...formattedValue,
      phone: formattedValue?.phone ?? '',
      loanAmount: { value: Number(formValue.price?.trim().replace(/\s/g, '')) ?? 0, uom: 'RUB' },
      productIds: [productId],
      regionCode: formValue.regions ?? undefined,
    }

    const getBodyByProductID = () => {
      if (!isMFOProduct) {
        const mappedBody = requestMapper?.(body) ?? body

        return mappedBody
      }

      const bodyMFO: CreateFundingRequestPrivateDto = {
        ...body,
        productIds: [FundingProduct.EXPRESS_MFO],
        regionCode: formValue.regions ?? undefined,
        city: '',
      }

      const mappedBody = requestMapper?.(bodyMFO) ?? bodyMFO

      return mappedBody
    }

    if (isAuthenticated) {
      const mappedBody = getBodyByProductID()

      if (isApplyingOfferRequestBody(mappedBody)) {
        createFundingRequest(mappedBody)
      }
    } else {
      setOptions({ formValues: {} })
      pushGtmEventOnRouteAuthFromFormOrder(userGtmInfo, userRegion.name, null, productId)

      openAuthModal({
        shouldSendCodeImmediately: true,
        shouldSkipSelectRoleStep: true,
        initialValues: { phone: formValue.phone },
        onCloseDialog: () => {
          setOptions({ formValues: body, apiRequest: createFundingRequest })
        },
        onCloseModal: () => {
          const mappedBody = getBodyByProductID()

          if (isApplyingOfferRequestBody(mappedBody)) {
            createFundingRequest(mappedBody)
          }
        },
      })
    }
  }

  const handleSelectCommunicationMethod = () => {
    updateFundingRequest({ fundingRequestId: fundingRequestId ?? undefined, communicationMethod: methods.getValues('communicationMethod') })
  }

  return (
    <section id={formId} className={classNames('LandingApplyingOfferAgentForm', className)}>
      <div className={classNames('LandingApplyingOfferAgentForm__wrapper', wrapperClassName)}>
        <FormProvider {...methods}>
          <LandingAnimation desktopTargetStyles={LANDING_ANIMATION.fade} isDisabled={!withAnimation}>
            <div>
              {formStatus === EFormStatus.FORM && (
                <form onSubmit={methods.handleSubmit(onSubmit)} className="LandingApplyingOfferAgentForm__form">
                  <div className="LandingApplyingOfferAgentForm__content">
                    <div className={classNames('LandingApplyingOfferAgentForm__rightBlock', rightBlockClassName)}>
                      <h3 className="LandingApplyingOfferAgentForm__title">{title}</h3>
                      <h5 className="LandingApplyingOfferAgentForm__description text_body-from-md font_base">{description}</h5>

                      <div className="LandingApplyingOfferAgentForm__customizedFormAgent">
                        <TextInput
                          rules={{
                            required: messages.field.error.requiredFieldPhone,
                          }}
                          name="phone"
                          type="tel"
                          isRequired
                          shouldBeTouchedBeforeError={false}
                          isDisabled={isAuthenticated}
                          placeholder={messages.field.phone.label}
                          modifiers={textInputModifiers}
                          control={control}
                        />
                        <RegionSelect
                          isSearchable
                          isRequired
                          control={control}
                          shouldBeTouchedBeforeError={false}
                          rules={{
                            required: messages.field.error.region,
                            validate: valueRegion => {
                              if (valueRegion !== null) {
                                return checkRegionForMFO(valueRegion, regionsAvailableExpressMFO, offerType)
                              }
                            },
                          }}
                          name="regions"
                          placeholder={messages.field.regions.label}
                          modifiers={regionSelectModifiers}
                        />
                        {withLoanAmount && (
                          <TextInput
                            name="price"
                            type="price"
                            shouldBeTouchedBeforeError={false}
                            isRequired
                            rules={{
                              required:
                                EFinancingOfferType.EXPRESS_MFO === offerType
                                  ? messages.field.error.requiredFieldPriceMFO
                                  : messages.field.error.requiredFieldPriceDefault,
                              min: {
                                value: EFinancingOfferType.EXPRESS_MFO === offerType ? DEFAULT_VALUE.minPriceMFO : DEFAULT_VALUE.minPrice,
                                message:
                                  EFinancingOfferType.EXPRESS_MFO === offerType
                                    ? messages.field.error.requiredMinPriceMFO
                                    : messages.field.error.requiredMinPrice,
                              },
                              ...(EFinancingOfferType.EXPRESS_MFO === offerType
                                ? {
                                    max: {
                                      value: DEFAULT_VALUE.maxPriceMFO,
                                      message: messages.field.error.requiredMaxPriceMFO,
                                    },
                                  }
                                : {}),
                            }}
                            placeholder={messages.field.name.label}
                            control={control}
                            modifiers={textInputModifiers}
                            priceCurrency={'руб'}
                          />
                        )}
                      </div>
                      {banner}
                      <div className="LandingApplyingOfferAgentForm__agreement">
                        <Checkbox
                          control={control}
                          id="agreement"
                          name="agreement"
                          isMobile={isMobile}
                          rules={{ required: messages.field.error.required }}
                          errorClassName="LandingApplyingOfferAgentForm__agreementError"
                        >
                          <div>
                            {formatLocalizedString(messages.form.agreement.text, {
                              agreement: (
                                <CommonLink
                                  key="registrationPolicy"
                                  url={getLegalDocumentUrl(legalDocuments, ELegalDocumentType.personalDataConsent)}
                                  shouldOpenInNewWindow
                                  className="underline underline_green color_green"
                                >
                                  {messages.form.agreement.agreement}
                                </CommonLink>
                              ),
                              policy: (
                                <CommonLink
                                  key="registrationRules"
                                  url={getLegalDocumentUrl(legalDocuments, ELegalDocumentType.registrationPolicy)}
                                  shouldOpenInNewWindow
                                  className="underline underline_green color_green"
                                >
                                  {messages.form.agreement.policy}
                                </CommonLink>
                              ),
                            })}
                          </div>
                        </Checkbox>
                      </div>
                      <div className="LandingApplyingOfferAgentForm__containerButton">
                        <Button
                          isDisabled={shouldDisableButton || isLoading}
                          type="submit"
                          modifiers={['full-width', 'green']}
                          className="LandingApplyingOfferAgentForm__button"
                        >
                          {submitButttonText}
                        </Button>
                      </div>
                    </div>
                  </div>
                </form>
              )}
              {formStatus === EFormStatus.COMMUNICATION_METHOD && (
                <LandingApplyingOfferPreferredConnections
                  className={communicationMethodClassName}
                  handleSelectedMethod={handleSelectCommunicationMethod}
                  isButtonDisable={isLoadingUpdate}
                />
              )}
              {formStatus === EFormStatus.SUCCESS && (
                <ApplyingFinancingAgentSuccessNew
                  className={successClassName}
                  buttonClassName={successButtonClassName}
                  descriptionClassName={successDescriptionClassName}
                  requestID={fundingRequestId}
                  navItems={navItems}
                  onReset={resetForm}
                />
              )}
              {formStatus === EFormStatus.FAIL && <ApplyingFinancingAgentFail className={errorClassName} onReset={resetForm} />}
            </div>
          </LandingAnimation>
        </FormProvider>
      </div>
    </section>
  )
}
